import StatCard from '@components/ui/StatCard';
import { Group, Stack, Text } from '@mantine/core';
import { neutralColors, semanticColors } from '@styles/design-system';
import { type FC } from 'react';

import { type OrderTotals } from './orderTotals';
import { formatCount } from '@utils/currency';

/**
 * The five figures of the open order book, shared by Home and Production Orders so the two
 * screens can never disagree about what is outstanding.
 *
 * READ LEFT TO RIGHT THEY ARE A SITE'S DAY: what was asked for, what is open to make, what is
 * standing here in each QC state, and what is still to send. The middle two are the holding —
 * made, not yet gone — so the bar answers "how much is left to build", "what have I got", and
 * "what is the brand still waiting for" without anyone doing arithmetic.
 *
 * NOT A PARTITION, and not meant to add up. Every unit is inside `Ordered` throughout; the two
 * QC figures are disjoint from each other but both sit inside `Still to send` until they leave.
 */
const OrderTotalsCards: FC<{ totals: OrderTotals; loading: boolean }> = ({
    totals,
    loading,
}) => (
    <Stack gap={8}>
        <Group gap={16} grow align="stretch">
            <StatCard
                label="Ordered"
                value={formatCount(totals.ordered)}
                helper="Units asked for"
                loading={loading}
            />
            <StatCard
                label="Open quantity"
                value={formatCount(totals.openUnits)}
                helper="Ordered minus what has been made"
                loading={loading}
            />
            {/*
              * The two QC states, coloured the way the job table colours them — cleared in
              * green, unruled in amber. Neither is a gate: both may be dispatched, and the
              * amber says "somebody should look", not "stuck".
              */}
            <StatCard
                label="QC approved"
                value={formatCount(totals.qcApproved)}
                helper="Passed QC and at manufacturer site"
                valueColor={semanticColors.positive.dark}
                loading={loading}
            />
            <StatCard
                label="QC pending"
                value={formatCount(totals.qcPending)}
                helper="QC yet to be done, at manufacturer site"
                valueColor={semanticColors.warning.dark}
                loading={loading}
            />
            {/*
              * WHAT IS LEFT TO SEND, not what has gone. A site reads this bar to plan the day,
              * and the two open questions are "what do I still have to make" and "what do I
              * still have to send" — cumulative output answers neither. What has already gone
              * is on every job row below for anyone reconciling.
              */}
            <StatCard
                label="Still to send"
                value={formatCount(totals.toSend)}
                helper="Ordered minus what has left the site"
                loading={loading}
            />
        </Group>
        {!loading && (
            <Text fz={12} c={neutralColors.textMuted}>
                Units across the open orders above. Completed and cancelled work is not
                counted.
            </Text>
        )}
    </Stack>
);

export default OrderTotalsCards;
